import { AbstractApiModel } from '@ng-cloud/badger-core/models/abstract-api-model';
import { Comment, Commentable, User } from '@ng-cloud/badger-core';

import * as _ from 'lodash';
import { ScanConfig } from '@ng-cloud/badger-core/models/scan-config';

export class Path extends AbstractApiModel<Path> implements Commentable {
  name: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  triggerDistance: number;
  robotDistance: number;
  speed: number;
  storeId: number;
  lightSegments: any;
  segmentSettings: any = {};
  active: boolean;
  activeRealogramId: number;
  realogramActivatedAt: Date;
  realogramActivatedById: number;
  lastExecutionPromotedBaseScan: boolean;
  sharedScanConfigIds: number[];

  comments: Comment[] = [];
  sortX: number;
  sortY: number;
  sortOrdinal: number;
  realogramActivatedBy: User;
  pathType: string;
  scanConfig: ScanConfig;

  deserialize(json: any): this {
    this.name = json.name;
    this.startX = +json.start_x;
    this.startY = +json.start_y;
    this.endX = +json.end_x;
    this.endY = +json.end_y;
    this.triggerDistance = +json.trigger_distance;
    this.storeId = +json.store_id;
    this.lightSegments = json.light_segments;
    this.robotDistance = +(json.robot_distance || 0);
    this.speed = +(json.speed || 0);
    this.segmentSettings = json.segment_settings;
    this.active = json.path_active;
    this.lastExecutionPromotedBaseScan = json.promoted_base_scan ? json.promoted_base_scan : false;
    this.activeRealogramId = json.active_realogram_id;
    this.realogramActivatedAt = json.realogram_activated_at && new Date(json.realogram_activated_at);
    this.realogramActivatedById = json.realogram_activated_by;
    this.pathType = json.path_type;

    const sortStartX = Math.min(this.startX, this.endX);
    const sortEndX = Math.max(this.startX, this.endX);
    const sortStartY = Math.min(this.startY, this.endY);
    const sortEndY = Math.max(this.startY, this.endY);
    const slope = (sortEndY - sortStartY) / (sortEndX - sortStartX);

    if (slope > 1) {
      this.sortX = sortStartX;
      this.sortY = Math.round(sortStartY);
    }
    else {
      this.sortX = Math.round(sortStartX);
      this.sortY = sortStartY;
    }

    return super.deserialize(json);
  }

  serialize(): any {
    return Object.assign(super.serialize(), {
      name: this.name,
      store_id: this.storeId,
      start_x: this.startX,
      start_y: this.startY,
      end_x: this.endX,
      end_y: this.endY,
      trigger_distance: this.triggerDistance,
      light_segments: this.lightSegments,
      robot_distance: this.robotDistance,
      speed: this.speed,
      path_type: this.pathType,
      segment_settings: this.segmentSettings,
      scan_config: (this.scanConfig != null) ? this.scanConfig.serialize() : null,
      shared_scan_config_ids: this.sharedScanConfigIds,
    });
  }

  sortArray(): string[] {
    return Path.generateSortArray(this.name);
  }

  getDisplayName(): string {
    return this.name;
  }

  getCommentableType(): string {
    return 'Path';
  }

  static generateSortArray(pathName: string): string[] {
    return _.chain(pathName.toLowerCase().trim())
      .split(/\W+/)
      .map(section => section.match(/\d+|\D+/g))
      .flatten()
      .map(section => /\d+/.test(section) ? section.padStart(5, '0') : section)
      .value();
  }

  static pathSort = (a: string[], b: string[]) => {
    for (let i = 0; i < a.length && i < b.length; i++) {
      if (!a[i]) {
        return 0;
      }
      const compareValue = a[i].localeCompare(b[i]);
      if (compareValue !== 0) {
        return compareValue;
      }
    }
    return a.toString().localeCompare(b.toString());
  };
}
